import { Formik, Form as FormikForm } from 'formik';
import React, { useEffect } from 'react';
import { Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { MultiSelect } from "react-multi-select-component";
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { getDepartmentUserApi } from '../../Redux/Action/InterestedTender';
import { userDepartmentStyles } from '../../Utils/Helper';
import Loader from '../../Utils/Loader';
import { addAssigneeSchema } from '../../Utils/Validations';
import './AddAssignee.css';

const AddAssignee = ({ show, onHide, loading, onSubmit, department_id, tenderLead }) => {
    const dispatch = useDispatch();

    const { loading: assigneeLoading, getAssigneesData, error } = useSelector((state) => state.getAssignees);
    const { loading: departmentLoading, getDepartmentUserData } = useSelector((state) => state.getUserOfDepartments);

    useEffect(() => {
        if (department_id) {
            const formData = new FormData();
            formData.append('department_id', department_id);

            dispatch(getDepartmentUserApi(formData))
        }
    }, [department_id])

    const getLabelForType = (type) => {
        switch (type) {
            case 'technical':
                return 'Technical Feasibility';
            case 'financial':
                return 'Financial Feasibility';
            case 'tax':
                return 'Tax Certificate';
            case 'other':
                return 'Other';
            default:
                return type.charAt(0).toUpperCase() + type.slice(1);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return dateString.split(' ')[0];
    };

    const formatAssigneesWithDepartment = (assignees) => {
        // Group assignees by department
        const groupedByDepartment = assignees.reduce((acc, assignee) => {
            const deptName = assignee.depart ? assignee.depart.name : 'Super Admin';
            if (!acc[deptName]) {
                acc[deptName] = [];
            }
            acc[deptName].push(assignee);
            return acc;
        }, {});

        // Sort departments alphabetically
        const sortedDepartments = Object.keys(groupedByDepartment).sort();

        // Create final sorted array with plain text labels
        const sortedAssignees = sortedDepartments.flatMap(deptName =>
            groupedByDepartment[deptName].map(assignee => ({
                value: assignee.value,
                labelName: `${assignee.label}`,  // Main label (user name)
                label: React.createElement(  // For display in dropdown
                    'div',
                    {
                        style: { display: 'flex', alignItems: 'center' }
                    },
                    React.createElement(
                        'span',
                        {
                            style: {
                                fontWeight: '500',
                                fontSize: '14px',
                                color: '#000'
                            }
                        },
                        assignee.label
                    ),
                    React.createElement(
                        'span',
                        {
                            style: {
                                fontSize: '12px',
                                color: '#666',
                                marginLeft: '4px'
                            }
                        },
                        ` - ${assignee?.depart ? assignee?.depart?.name : 'Super Admin'}`
                    )
                )
            }))
        );

        return sortedAssignees;
    };

    const getInitialAssignments = () => {
        if (!getAssigneesData) return [];

        const categories = ['technical', 'financial', 'tax', 'other'];
        return categories.map(category => {
            const assignees = getAssigneesData?.data[category] || [];
            const sortedAssigneeOptions = formatAssigneesWithDepartment(assignees);
            const defaultSelected = assignees.filter(a => a.tender_assign !== null);
            const disabledOptions = assignees.filter(a => a.tender_document !== null);

            return {
                type: category,
                assignees: defaultSelected,
                assigneeList: sortedAssigneeOptions,
                disabledOptions: disabledOptions,
                deadline: ''
            };
        });
    };

    const initialValues = {
        tender_lead: tenderLead?.id || '', // Set initial value from tenderLead
        assignments: getInitialAssignments()
    };

    const departmentOptions = getDepartmentUserData?.data?.map((d) => ({
        value: d?.value,
        label: React.createElement(
            'div',
            {
                style: { display: 'flex', alignItems: 'center' }
            },
            React.createElement(
                'span',
                {
                    style: {
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#000'
                    }
                },
                d?.label
            ),
            React.createElement(
                'span',
                {
                    style: {
                        fontSize: '12px',
                        color: '#666',
                        marginLeft: '4px'
                    }
                },
                ` - ${d?.depart ? d?.depart?.name : 'Super Admin'}`
            )
        )
    })) || [];

    // If tenderLead exists, add it to options if not already present
    if (tenderLead && !departmentOptions.some(option => option.value === tenderLead.id)) {
        departmentOptions.unshift({
            value: tenderLead.id,
            label: tenderLead.user_name
        });
    }

    return (
        <Modal centered className='doc_type' size='lg' show={show}>
            <Modal.Body>
                <div className='add_doc_type'>
                    <div className='head'>
                        <h5>Add Assignee</h5>
                    </div>
                    {assigneeLoading ? <Loader /> : (
                        <Formik
                            initialValues={initialValues}
                            validationSchema={addAssigneeSchema}
                            onSubmit={(values, { setSubmitting }) => {
                                // Prevent default form behavior
                                // event.preventDefault();
                                // event.stopPropagation();
                                
                                setSubmitting(false);
                                onSubmit(values);
                            }}
                            validateOnMount={false}
                            validateOnChange={true}
                            validateOnBlur={true}
                        >
                            {({ values, errors, touched, setFieldValue, isSubmitting, handleSubmit }) => (
                                <FormikForm 
                                    className='add_assignee_form' 
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSubmit(e);
                                    }}
                                >
                                    <div className='head_list_type mb-1'>
                                        <span>1</span>
                                        <h6>Tender Lead</h6>
                                    </div>
                                    <Row className='px-5'>
                                        <Col md={7}>
                                            <Form.Group className="form_field">
                                                <Form.Label>Select Tender Lead <span className="required">*</span></Form.Label>
                                                <Select
                                                    isLoading={departmentLoading}
                                                    options={departmentOptions}
                                                    placeholder="Select Tender Lead"
                                                    styles={userDepartmentStyles}
                                                    className={`assignee_multi_select ${touched.tender_lead && errors.tender_lead ? 'is-invalid' : ''}`}
                                                    onChange={(selected) => setFieldValue('tender_lead', selected?.value)}
                                                    value={departmentOptions.find(option => option.value === values.tender_lead)}
                                                    defaultValue={tenderLead ? {
                                                        value: tenderLead.id,
                                                        label: tenderLead.user_name
                                                    } : null}
                                                />
                                                {touched.tender_lead && errors.tender_lead && (
                                                    <div className="error-message">{errors.tender_lead}</div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {values.assignments.map((assignment, index) => {
                                        return (
                                            <div key={index}>
                                                <div className='head_list_type'>
                                                    <span>{(index + 1) + 1}</span>
                                                    <h6>{getLabelForType(assignment.type)}</h6>
                                                </div>
                                                <Row className='px-5'>
                                                    <Col md={7} className="mb-3">
                                                        <Form.Group>
                                                            <Form.Label>
                                                                <div className='add_assignee_label'>
                                                                    Assignee
                                                                </div>
                                                            </Form.Label>
                                                            <MultiSelect
                                                                options={assignment?.assigneeList || []}
                                                                value={assignment.assignees}
                                                                loading={assigneeLoading}
                                                                onChange={(selected) => setFieldValue(`assignments.${index}.assignees`, selected)}
                                                                labelledBy="Select"
                                                                className='assignee_multi_select'
                                                                valueRenderer={(selected) => {
                                                                    return selected.map(option => option.labelName ? option.labelName : option.label).join(', ');
                                                                }}
                                                            />
                                                            
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={5} className="mb-3">
                                                        <Form.Group>
                                                            <Form.Label>Deadline</Form.Label>
                                                            <div
                                                                style={{ position: 'relative' }}
                                                                onClick={() => {
                                                                    const dateInput = document.getElementById(`date-input-${index}`);
                                                                    if (dateInput) {
                                                                        dateInput.showPicker();
                                                                    }
                                                                }}
                                                            >
                                                                <Form.Control
                                                                    id={`date-input-${index}`}
                                                                    type="date"
                                                                    defaultValue={assignment.assignees.length > 0 ? formatDate(assignment?.assignees[0]?.tender_assign?.due_date) : ''}
                                                                    onChange={(e) =>
                                                                        setFieldValue(`assignments.${index}.deadline`, e.target.value)
                                                                    }
                                                                    min={new Date().toISOString().split('T')[0]}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                />
                                                            </div>
                                                            
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )
                                    })}
                                    <div className='add_assignee_btn'>
                                        <button type="button" onClick={onHide}>Cancel</button>
                                        <button
                                            type="button" // Change from submit to button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSubmit(e);
                                            }}
                                            disabled={loading || isSubmitting}
                                        >
                                            {loading ? <Spinner animation='border' size='sm' /> : "Submit"}
                                        </button>
                                    </div>
                                </FormikForm>
                            )}
                        </Formik>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AddAssignee;